<script setup lang="ts">
/**
 * PUT /deployments/{slug}/knowledge-bases. The request carries the full set of collections the
 * endpoint consults by default; an empty set turns memory off. Collections come from the
 * deployment's project.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import PickList from '~/components/ui/PickList.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import StatusCell from '~/components/ui/StatusCell.vue'

const props = defineProps<{ deployment: components['schemas']['Deployment']; memory: { id: string; name: string; facts: string; status: string }[] }>()
const emit = defineEmits<{ close: []; submit: [payload: { knowledgeBaseIds: string[] }] }>()
const bound = ref<string[]>([...props.deployment.knowledgeBaseIds])
const toggle = (id: string) => { bound.value = bound.value.includes(id) ? bound.value.filter((entry) => entry !== id) : [...bound.value, id] }
const changed = computed(() => bound.value.length !== props.deployment.knowledgeBaseIds.length || bound.value.some((id) => !props.deployment.knowledgeBaseIds.includes(id)))
const line = computed(() => bound.value.length === 0 ? 'answers come from the weights alone' : `${bound.value.length} ${bound.value.length === 1 ? 'collection' : 'collections'} consulted by default`)
</script>

<template>
  <Dialog title="Collections consulted" :meta="'/' + deployment.slug" :width="520" @close="emit('close')">
    <template #icon><Icon name="memory" /></template>
    <p v-if="!memory.length" class="m-0 text-xs text-muted-foreground">This project has no collections yet. A collection is made when a document is added to one, from the document's own menu.</p>
    <PickList v-else v-slot="{ shown }" :items="memory" unit="collections" :chosen="bound.length">
      <Choice v-for="entry in shown" :key="entry.id" multiple :title="entry.name" :facts="entry.facts" :selected="bound.includes(entry.id)" @click="toggle(entry.id)">
        <span class="ml-auto shrink-0"><StatusCell :status="entry.status" /></span>
      </Choice>
    </PickList>
    <p class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">{{ line }}</p>
    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton label="Save binding" variant="primary" :disabled="!changed" @click="emit('submit', { knowledgeBaseIds: bound })"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
